import React, { FC, useEffect, useRef, useState } from 'react';
import {
	Button,
	Checkbox,
	Input,
	LinearProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
} from '@mui/material';
import { columnsMeters } from './column';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setErrorApp, setInfoApp } from '../../store/reducers/InfoSlice';
import {
	setLoading,
	setMeters,
	setTotalSizeMeters,
	setPageMeters,
	setSizePerPageCalls,
	setSort,
	setStart,
	setSearch,
	setCounterName,
	setServiceName,
	setLsNumber
} from '../../store/reducers/MetersSlice';
import TablePagination from '@mui/material/TablePagination';
import 'rsuite/dist/rsuite.min.css';
import XLSX from 'xlsx';
import './tableMeters.css';
import {
	deleteMeters,
	returnMeters,
	returnMetersExcel,
} from '../../service/MetersService';
import { Delete, FileUpload } from '@mui/icons-material';
import { clearDB, downloadExcelFile, downloadZipFile, testAccounts } from '../../service/UsersService';
import { setCacheModal } from '../../store/reducers/ModalSlice';
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogTitle } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import { fetchProfile } from '../../service/ProfileServiceThunk';
import moment from 'moment';

const TableMeters: FC = () => {
	const dispatch = useAppDispatch();
	const { isLoading, loadingProgress, meters, sort, page, totalSize, sizePerPage, search, counterName, serviceName, lsNumber } =
		useAppSelector((state) => state.metersReducer);
	const [selected, setSelected] = useState<string[]>([]);
	const [selectedAll, setSelectedAll] = useState<boolean>(false);
	const [loadingClearDb, setLoadingClearDb] = useState<boolean>(false);
	const [openModal, setOpenModal] = useState(false);

	const inputEl = useRef<HTMLInputElement | null>(null);
	const inputElExcel = useRef<HTMLInputElement | null>(null);

	const handleFile = async ({ target }: any) => {
		if (target.files.length) {
			dispatch(setLoading(true));
			const response = await downloadZipFile(target.files[0]);
			if (response.error) {
				dispatch(setLoading(false));
				dispatch(
					setErrorApp({
						isErrorApp: true,
						errorApp: `Произошла ошибка при импорте счетчиков ${response.error.response.data?.file ? response.error.response.data?.file : ''}`,
					})
				);
			} else {
				dispatch(setLoading(false));
				dispatch(
					setInfoApp({ isInfoApp: true, infoApp: 'Данные успешно загружены' })
				);
				await loadMeters();
			}
		}
	};

	const handleFileExcel = async ({ target }: any) => {
		if (target.files.length) {
			const response = await downloadExcelFile(target.files[0]);
			if (response.error) {
				dispatch(
					setErrorApp({
						isErrorApp: true,
						errorApp: 'Произошла ошибка при импорте счетчиков',
					})
				);
			} else {
				dispatch(
					setInfoApp({ isInfoApp: true, infoApp: 'Данные успешно загружены' })
				);
			}
		}
	};

	const handleSubmit = async (evt: any) => {
		evt.preventDefault();
		inputEl.current!.click();
	};

	const loadMeters = async () => {
		dispatch(setLoading(true));
		const response = await returnMeters(page, sizePerPage, sort, search);
		if (response.error) {
			dispatch(setLoading(false));
			dispatch(
				setErrorApp({
					isErrorApp: true,
					errorApp: 'Произошла ошибка при получении списка лицевых счетов',
				})
			);
		} else {
			dispatch(setLoading(false));
			const { meta, data } = response.data.data;
			dispatch(setMeters(data));
			dispatch(setTotalSizeMeters(meta.total));
		}
	};

	const returnMetersForJSON = async () => {
		dispatch(setLoading(true));
		const response = await returnMetersExcel();
		if (response.error) {
			dispatch(setLoading(false));
			dispatch(
				setErrorApp({
					isErrorApp: true,
					errorApp: 'Произошла ошибка при экспорте показаний счетчиков',
				})
			);
		} else {
			dispatch(setLoading(false));
			const { data } = response.data;
			const fileName = 'export';
			const link = document.createElement('a');
			const blob = new Blob([JSON.stringify(data)], {
				type: 'application/octet-stream',
			});
			link.href = window.URL.createObjectURL(blob);
			link.download = fileName + '.json';
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		}
	};

	const returnMetersForExport = () => {
		(async () => {
			dispatch(setLoading(true));
			const deleteKey: string[] = ['id', 'acc_id'];
			const translateKey = [
				['date', 'Последнее обновление'],
				['name', 'Наименование счётчика'],
				['values', 'Тариф'],
				['capacity', 'Разрядность'],
				['service', 'Сервис'],
				['val1', 'Показания 1'],
				['val2', 'Показания 2'],
				['val3', 'Показания 3'],
			];
			const response = await returnMetersExcel();
			if (response.error) {
				dispatch(setLoading(false));
				dispatch(
					setErrorApp({
						isErrorApp: true,
						errorApp: 'Произошла ошибка при экспорте показаний счетчиков',
					})
				);
			} else {
				dispatch(setLoading(false));
				const { data } = response.data;
				for (const meter of data) {
					for (const key of deleteKey) {
						delete meter[key];
					}
					for (const elem of translateKey) {
						delete Object.assign(meter, { [elem[1]]: meter[elem[0]] })[elem[0]];
					}
				}
				const workSheet = XLSX.utils.json_to_sheet(data);
				const workBook = XLSX.utils.book_new();
				XLSX.utils.book_append_sheet(workBook, workSheet, 'meters');
				XLSX.writeFile(workBook, 'meters.xlsx');
			}
		})();
	};

	const handleDelete = async () => {
		dispatch(setLoading(true));
		const promises: void[] = [];
		for (const item of selected) {
			const response = await deleteMeters(item);
			promises.push(response);
		}
		Promise.all(promises).then(async () => {
			dispatch(setLoading(false));
			dispatch(setInfoApp({ isInfoApp: true, infoApp: 'Данные удалены' }));
			setSelectedAll(false);
			setSelected([]);
			await loadMeters();
		});
	};

	useEffect(() => {
		(async () => {
			await loadMeters();
		})();
	}, [page, sort, sizePerPage, search, dispatch]);

	useEffect(() => {
		selected.length === 0 ? setSelectedAll(false) : setSelectedAll(true);
	}, [selected]);

	const handleClick = (event: any, id: string) => {
		const selectedIndex = selected.indexOf(id);
		let newSelected: string[] = [];
		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selected, id);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selected.slice(1));
		} else if (selectedIndex === selected.length - 1) {
			newSelected = newSelected.concat(selected.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selected.slice(0, selectedIndex),
				selected.slice(selectedIndex + 1)
			);
		}
		setSelected(newSelected);
	};
	const checkSelected = (id: string) => selected.indexOf(id) !== -1;

	const onSelectAllClick = (event: any) => {
		if (event.target.checked && meters.length > 0) {
			setSelectedAll(true);
			setSelected(
				meters.map(
					(row) =>
						`${row.acc_id.data_source_id}_${row.acc_id.data_source_id}_${row.acc_id.id}_${row.id}`
				)
			);
			return;
		}
		setSelected([]);
		setSelectedAll(false);
	};

	const handleChangePage = (event: any, newPage: any) => {
		dispatch(setPageMeters(newPage + 1));
		dispatch(setStart(newPage * sizePerPage));
	};

	const handleChangeRowsPerPage = (e: any) => {
		dispatch(setSizePerPageCalls(e.target.value));
		dispatch(setPageMeters(1));
		dispatch(setStart(0));
	};

	const createSortHandler = (e: any, field: string) => {
		if (
			e.target.tagName === 'svg' ||
			e.target.tagName === 'SPAN' ||
			e.target.tagName === 'path'
		) {
			const isAsc = sort.sortField === field && sort.sortOrder === 'asc';
			dispatch(setPageMeters(1));
			dispatch(
				setSort({
					sortOrder: isAsc ? 'desc' : 'asc',
					sortField: field,
				})
			);
		}
	};

	// @ts-ignore
	const customLabel = ({ from, to, count }) => {
		return `${from}-${to} из ${count}`;
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const debounce = <T extends (...args: any[]) => any>(callback: T, waitFor: number) => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		let timeout: any = 0;
		return (...args: Parameters<T>): ReturnType<T> => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			let result: any;
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				result = callback(...args);
			}, waitFor);
			return result;
		};
	};

	const setValue = (type: string) => {
		if (type === 'name') {
			return counterName;
		} else if (type === 'service') {
			return serviceName;
		} else if (type === 'acc_id') {
			return lsNumber;
		}
		return '';
	};

	const setNewValue = (field: string, value: string) => {
		if (field === 'name') {
			dispatch(setCounterName(value));
		} else if (field === 'service') {
			dispatch(setServiceName(value));
		} else if (field === 'acc_id') {
			dispatch(setLsNumber(value));
		}
	};

	const handleChangeInput = (
		evt: React.ChangeEvent<HTMLInputElement>,
		type: string
	) => {
		const obj: any = {};
		obj[type] = evt.target.value;
		dispatch(setSearch(obj));
	};

	const debouncedHandle = debounce(handleChangeInput, 1000)

	const onTestAccounts = async () => {
		const now = new Date();
		if (isLoading) return;
		const hasCalled = false;
		const startDateGetter = new Date(now.getTime() - 2592000000);
		startDateGetter.setHours(0);
		startDateGetter.setMinutes(0);
		startDateGetter.setSeconds(0);
		const endDateGetter = now;
		endDateGetter.setHours(23);
		endDateGetter.setMinutes(59);
		endDateGetter.setSeconds(59);
		(async () => {
			dispatch(setLoading(true));
			const deleteKey: string[] = ['address', 'debt', 'owner', 'data_source_id', 'group', 'id'];
			const translateKey = [
				['number', 'Лицевой счет'],
				['phone', 'Телефон'],
			];
			const translateKeyMeters = [
				['date', 'Последнее обновление'],
				['name', 'Наименование счётчика'],
				['values', 'Тариф'],
				['service', 'Сервис'],
				['val1', 'Показания 1'],
				['val2', 'Показания 2'],
				['val3', 'Показания 3'],
			];
			const response = await testAccounts({
				startDateGetter: startDateGetter.toISOString(),
				endDateGetter: endDateGetter.toISOString(),
				hasCalled
			});
			if (response.error === 'No record found with the specified `id`.') {
				setLoading(false);
				return dispatch(
					setErrorApp({
						isErrorApp: true,
						errorApp: 'Не найдена запись с указанным id',
					})
				);
			}
			if (response.error) {
				setLoading(false);
				return dispatch(
					setErrorApp({
						isErrorApp: true,
						errorApp: 'Произошла ошибка при проверке списка',
					})
				);
			} else {
				const { users } = response.data.data;
				const counters = [];
				for (const account of users) {
					for (const key of deleteKey) {
						delete account[key];
					}
					for (const elem of translateKey) {
						delete Object.assign(account, { [elem[1]]: account[elem[0]] })[elem[0]];
					}
					for (const value of account.meters) {
						const qwerty = {};
						for (const elem of translateKeyMeters) {
							Object.assign(qwerty, { [elem[1]]: value[elem[0] as keyof typeof value] });
						}
						delete Object.assign(qwerty, account)['meters'];
						counters.push(qwerty)
					}
				}

				dispatch(setLoading(false));
				let message: string;
				if (users === null) {
					message = 'Произошли проблемы обратитесь в техподдержку';
				} else {
					message = `Статус проверки списка: "Показания не переданы", \n количество счетчиков: "${counters.length}", \n количество лицевых счетов: "${users.length}"`;
				}
				dispatch(setInfoApp({ isInfoApp: true, infoApp: message, isExport: true, data: counters }));
			}
		})();
	};

	const showModal = () => {
		dispatch(
			setCacheModal({
				show: true,
			})
		);
	};

	const handleClickOpenModal = () => {
		setOpenModal(true);
	};

	const handleCloseModal = () => {
		setOpenModal(false);
	};

	const onClearDB = async () => {
		setOpenModal(false);
		setLoadingClearDb(true);
		const response = await clearDB();
		if (response.error) {
			setLoadingClearDb(false);
			dispatch(
				setErrorApp({
					isErrorApp: true,
					errorApp: 'При удаление данных произошла ошибка',
				})
			);
		} else {
			setLoadingClearDb(false);
			dispatch(
				setInfoApp({ isInfoApp: true, infoApp: 'Данные успешно удалены' })
			);
			dispatch(fetchProfile());
		}
	};

	return (
		<div className="container">
			<p className="label">Управление показаниями счётчиков</p>
			<Paper sx={{ width: '100%', overflow: 'hidden' }}>
				<TableContainer className="tableContainer">
					<Table className="table" stickyHeader aria-label="sticky table">
						<TableHead>
							<TableRow className="row_header">
								<TableCell
									style={{
										width: 50,
									}}
								>
									<Checkbox checked={selectedAll} onChange={onSelectAllClick} />
								</TableCell>
								{columnsMeters.map((column) => (
									<TableCell
										align="center"
										key={column.field}
										padding="normal"
										style={{
											width: column.width,
										}}
										// @ts-ignore
										sortDirection={
											sort.sortField === column.field ? sort.sortOrder : 'asc'
										}
									>
										{column.headerName}
										{column.sort ? (
											<TableSortLabel
												key={column.field}
												active={sort.sortField === column.field}
												// @ts-ignore
												direction={
													sort.sortField === column.field
														? sort.sortOrder
														: 'asc'
												}
												onClick={(e) => createSortHandler(e, column.field)}
											/>
										) : null}
										{column.input ? (
											<Input
												key={`${column.field}_input`}
												className={column.input ? 'tableCell_input' : ''}
												onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
													debouncedHandle(evt, `${column.field}`);
													setNewValue(`${column.field}`, evt.target.value);
												}}
												aria-describedby="component-helper-text"
												value={setValue(`${column.field}`)}
											/>
										) : null}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{meters.length > 0 ? (
								meters.map((row, index) => {
									const isSelected = checkSelected(
										`${row?.acc_id?.data_source_id}_${row?.acc_id?.data_source_id}_${row?.acc_id?.id}_${row?.id}`
									);
									return (
										<TableRow
											className={index % 2 ? 'row_background' : ''}
											key={`${row?.acc_id?.data_source_id}_${row?.acc_id?.data_source_id}_${row?.acc_id?.id}_${row?.id}`}
											hover
											onClick={(event) =>
												handleClick(
													event,
													`${row?.acc_id?.data_source_id}_${row?.acc_id?.data_source_id}_${row?.acc_id?.id}_${row?.id}`
												)
											}
											role="checkbox"
											aria-checked={isSelected}
											tabIndex={-1}
											selected={isSelected}
										>
											<TableCell className="selectCheckbox" padding="checkbox">
												<Checkbox
													onClick={(event) =>
														handleClick(
															event,
															`${row?.acc_id?.data_source_id}_${row?.acc_id?.data_source_id}_${row?.acc_id.id}_${row?.id}`
														)
													}
													className="selectCheckbox"
													checked={isSelected}
												/>
											</TableCell>
											<TableCell align="center">{row?.name}</TableCell>
											<TableCell align="center">{row?.values}</TableCell>
											<TableCell align="center">{row?.capacity}</TableCell>
											<TableCell align="center">{row?.service}</TableCell>
											<TableCell align="center">{moment(row?.date).format('YYYY.MM.DD HH:mm:ss')}</TableCell>
											<TableCell align="center">
												{row?.acc_id?.number}
											</TableCell>
											<TableCell align="center">{row?.val1}</TableCell>
											<TableCell align="center">{row?.val2}</TableCell>
											<TableCell align="center">{row?.val3}</TableCell>
											<TableCell align="center">
												{row?.from_phone ? 'Подтвержден' : 'Не подтвержден'}
											</TableCell>
										</TableRow>
									);
								})
							) : (
								<TableRow />
							)}
						</TableBody>
					</Table>
					{meters.length > 0 || isLoading ? null : (
						<p className="label-no_records">Нет записей</p>
					)}
				</TableContainer>
			</Paper>
			{isLoading && (loadingProgress < 100 && loadingProgress > 0) ? <LinearProgress variant="determinate" value={loadingProgress}/> : isLoading ? <LinearProgress/> : null}
			<div className="wrapper-footer row_background">
				<div className="wrapper-buttons">
					{selected.length > 0 ? (
						<Button
							disableElevation
							className="delete-button"
							onClick={handleDelete}
							startIcon={<Delete />}
							component="span"
						>
							Удалить
						</Button>
					) : null}
					{meters.length > 0 ? (
						<button className="button-export" onClick={returnMetersForExport}>
							Экспорт(excel)
						</button>
					) : null}
					{meters.length > 0 ? (
						<button
							className="button-export"
							onClick={onTestAccounts}>
							Экспорт(показания не переданы)
						</button>
					) : null}
					{meters.length > 0 ? (
						<button
							className="button-export"
							onClick={returnMetersForJSON}
						>
							Экспорт(json)
						</button>
					) : null}
					<input
						ref={inputEl}
						className="input-file"
						onChange={handleFile}
						type="file"
					/>
					<Button
						className="button-upload"
						onClick={(evt: any) => handleSubmit(evt)}
						startIcon={<FileUpload />}
						component="span"
					>
						Импорт(*.zip)
					</Button>
					<button className="button-export" onClick={showModal}>
						Очистить кеш
					</button>
					<input
						ref={inputElExcel}
						className="input-file"
						onChange={handleFileExcel}
						type="file"
					/>
					{meters.length > 0 ? (
						<div>
							<LoadingButton
								loading={loadingClearDb}
								onClick={handleClickOpenModal}
								className="button-export"
							>
								Очистить данные
							</LoadingButton>
							<Dialog
								open={openModal}
								keepMounted
								onClose={handleCloseModal}
								aria-describedby="alert-dialog-slide-description"
							>
								<DialogTitle className="title_modal">
									{
										'Вы уверены, что хотите удалить все данные лицевых счетов и счетчиков?'
									}
								</DialogTitle>
								<DialogActions>
									<Button onClick={handleCloseModal}>Отмена</Button>
									<Button onClick={onClearDB}>Очистить данные</Button>
								</DialogActions>
							</Dialog>
						</div>
					) : null}
				</div>
				<TablePagination
					rowsPerPageOptions={[10, 20, 50, 100, 500, 5000]}
					component="div"
					count={totalSize}
					rowsPerPage={sizePerPage}
					page={page - 1}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
					labelRowsPerPage="На странице:"
					labelDisplayedRows={customLabel}
				/>
			</div>
		</div>
	);
};

export default TableMeters;
